import { useEffect, useState, useRef } from "react";
import {
  Mail, Upload, Plus, Trash2, Loader2, CheckCircle2, XCircle,
  Link2, User, Building2, Copy, Download,
} from "lucide-react";
import { toast } from "sonner";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const EMPTY_ROW = () => ({
  id: Math.random().toString(36).slice(2),
  linkedin_url: "",
  first_name: "",
  last_name: "",
  domain: "",
  state: "idle",         // idle | pending | settled | refunded | error
  email: null,
  email_status: null,
  lookup_id: null,
  source: null,
  reason: null,
});

export default function FindEmail() {
  const { user, refresh } = useAuth();
  const [mode, setMode] = useState("manual"); // "manual" | "csv"
  const [rows, setRows] = useState([EMPTY_ROW(), EMPTY_ROW()]);
  const [running, setRunning] = useState(false);
  const [usage, setUsage] = useState(null);
  const fileRef = useRef(null);

  const loadUsage = async () => {
    try {
      const { data } = await http.get("/usage");
      setUsage(data);
    } catch (_) { /* ignore */ }
  };
  useEffect(() => { loadUsage(); }, []);

  // Available enrichment credits from `/api/usage`:
  //   limit=null            → truly unlimited (enterprise / no cap)
  //   limit=<n>             → monthly plan cap
  //   purchased_credits=<n> → one-time credit packs stacked on top
  // Remaining = (limit + purchased_credits) - used, floored at 0.
  const isUnlimited = usage != null && usage.limit == null;
  const effectiveLimit = (usage?.limit ?? 0) + (usage?.purchased_credits ?? 0);
  const creditsRemaining = isUnlimited
    ? Infinity
    : Math.max(0, effectiveLimit - (usage?.used ?? 0));

  const rowIsValid = (r) =>
    !!(r.linkedin_url?.trim()) ||
    (r.first_name?.trim() && r.last_name?.trim() && r.domain?.trim());
  const validRowCount = rows.filter(rowIsValid).length;

  const updateRow = (id, patch) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, EMPTY_ROW()]);
  const removeRow = (id) =>
    setRows((rs) => (rs.length === 1 ? [EMPTY_ROW()] : rs.filter((r) => r.id !== id)));
  const clearAll = () => setRows([EMPTY_ROW()]);

  // Poll a single lookup until terminal state or ~3 min timeout.
  // 10s cadence keeps us well under the provider's rate cap during bulk runs.
  const pollLookup = async (rowId, lookupId) => {
    const started = Date.now();
    while (Date.now() - started < 180000) {
      await new Promise((r) => setTimeout(r, 10000));
      try {
        const { data } = await http.get(`/email-lookups/${lookupId}`);
        if (data.state === "settled") {
          updateRow(rowId, { state: "settled", email: data.email });
          return true;
        }
        if (data.state === "refunded") {
          updateRow(rowId, { state: "refunded", reason: data.reason });
          return false;
        }
        if (data.state === "error") {
          updateRow(rowId, { state: "error", reason: data.reason });
          return false;
        }
      } catch (_) { /* transient — keep polling */ }
    }
    updateRow(rowId, { state: "error", reason: "timeout" });
    return false;
  };

  const runOne = async (row) => {
    updateRow(row.id, { state: "pending", email: null, reason: null });
    try {
      const { data } = await http.post("/email-lookups", {
        linkedin_url: row.linkedin_url || null,
        first_name: row.first_name || null,
        last_name: row.last_name || null,
        domain: row.domain || null,
      });
      if (data.state === "settled") {
        updateRow(row.id, {
          state: "settled",
          email: data.email,
          email_status: data.email_status,
          source: data.source,
        });
        return;
      }
      if (data.state === "pending") {
        updateRow(row.id, { lookup_id: data.lookup_id });
        await pollLookup(row.id, data.lookup_id);
        return;
      }
      updateRow(row.id, { state: "refunded", reason: data.reason });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Lookup failed";
      updateRow(row.id, { state: "error", reason: msg });
    }
  };

  const runAll = async () => {
    const targets = rows.filter(rowIsValid);
    if (targets.length === 0) {
      toast.error("Add at least one contact to look up.");
      return;
    }
    if (creditsRemaining < targets.length) {
      toast.error(
        `Need ${targets.length} enrichment credits — you have ${creditsRemaining}. Buy more or upgrade your plan.`
      );
      return;
    }
    setRunning(true);
    const queue = [...targets];
    const workers = Array.from({ length: Math.min(5, queue.length) }, async () => {
      while (queue.length) {
        const next = queue.shift();
        await runOne(next);
        loadUsage();
      }
    });
    await Promise.all(workers);
    setRunning(false);
    const found = rows.filter((r) => r.state === "settled").length;
    toast.success(`Bulk lookup complete · ${found}/${targets.length} emails found.`);
  };

  const onCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) {
      toast.error("CSV must have a header row + at least one data row.");
      return;
    }
    const header = lines[0].toLowerCase().split(",").map((s) => s.trim());
    const idx = {
      first: header.findIndex((h) => /^(first[_\s-]?name|firstname|first)$/.test(h)),
      last: header.findIndex((h) => /^(last[_\s-]?name|lastname|last|surname)$/.test(h)),
      domain: header.findIndex((h) => /^(domain|company[_\s-]?domain|website)$/.test(h)),
      linkedin: header.findIndex((h) => /linkedin/.test(h)),
    };
    const parsed = lines.slice(1).map((line) => {
      const cols = line.split(",").map((s) => s.trim());
      return {
        ...EMPTY_ROW(),
        first_name: idx.first >= 0 ? cols[idx.first] || "" : "",
        last_name: idx.last >= 0 ? cols[idx.last] || "" : "",
        domain: idx.domain >= 0 ? cols[idx.domain] || "" : "",
        linkedin_url: idx.linkedin >= 0 ? cols[idx.linkedin] || "" : "",
      };
    });
    const validParsed = parsed.filter(rowIsValid);
    if (validParsed.length === 0) {
      toast.error("No valid rows found. Include First name + Last name + Company domain OR a LinkedIn URL.");
      return;
    }
    setRows(validParsed);
    setMode("csv");
    toast.success(`Loaded ${validParsed.length} contacts from CSV.`);
    if (fileRef.current) fileRef.current.value = "";
  };

  const copyEmail = (email) => {
    navigator.clipboard.writeText(email);
    toast.success("Copied");
  };

  // Rows that have been processed (settled, refunded, or error)
  const completedRows = rows.filter(
    (r) => r.state === "settled" || r.state === "refunded" || r.state === "error"
  );
  const foundCount = rows.filter((r) => r.state === "settled").length;

  const downloadCsv = () => {
    if (completedRows.length === 0) {
      toast.error("No results to download yet.");
      return;
    }
    const esc = (v) => {
      const s = (v ?? "").toString();
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = [
      "first_name", "last_name", "domain", "linkedin_url",
      "email", "email_status", "source", "status", "reason",
    ];
    const statusOf = (r) =>
      r.state === "settled" ? "found" :
      r.state === "refunded" ? "not_found" :
      r.state === "error" ? "error" : "";
    const lines = [
      header.join(","),
      ...completedRows.map((r) =>
        [
          r.first_name,
          r.last_name,
          r.domain,
          r.linkedin_url,
          r.email || "",
          r.email_status || "",
          r.source || "",
          statusOf(r),
          r.reason || "",
        ].map(esc).join(",")
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
    a.download = `leadiq-email-lookups-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${completedRows.length} row${completedRows.length === 1 ? "" : "s"}.`);
  };

  return (
    <div className="min-h-screen bg-ink-50" data-testid="find-email-page">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <header className="mb-8">
          <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-1">
            Find verified work emails · powered by LeadIQ
          </div>
          <div className="flex items-end justify-between gap-6">
            <div>
              <h1 className="font-display font-extrabold text-3xl text-ink-900 mb-1.5">
                Find Email Address
              </h1>
              <p className="text-sm text-ink-500 max-w-2xl">
                Look up work emails one at a time or in bulk from a CSV. Uses 1 enrichment credit per settled result — no charge on miss.
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs shrink-0 pb-1">
              <span className="text-ink-400 font-mono uppercase tracking-widest">Credits</span>
              <span className="font-mono text-ink-900 font-semibold text-sm" data-testid="credits-balance">
                {isUnlimited ? "∞" : creditsRemaining}
              </span>
            </div>
          </div>
        </header>

        {/* Mode tabs + Clear all */}
        <div className="bg-white border border-ink-100 rounded-sm p-4 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="tab-manual"
              onClick={() => setMode("manual")}
              className={`px-3 py-1.5 rounded-sm text-sm font-semibold transition-colors ${
                mode === "manual" ? "bg-ink-100 text-ink-900" : "text-ink-500 hover:bg-ink-50"
              }`}
            >
              Manual
            </button>
            <label
              data-testid="tab-csv"
              className={`px-3 py-1.5 rounded-sm text-sm font-semibold transition-colors cursor-pointer inline-flex items-center gap-1.5 ${
                mode === "csv" ? "bg-ink-100 text-ink-900" : "text-ink-500 hover:bg-ink-50"
              }`}
            >
              <Upload className="w-3.5 h-3.5" /> CSV File
              <input
                type="file"
                ref={fileRef}
                accept=".csv,text/csv"
                onChange={onCsvUpload}
                className="hidden"
              />
            </label>
          </div>
          <Button
            variant="outline"
            onClick={clearAll}
            data-testid="clear-all"
            className="h-8 rounded-sm border-ink-200 text-ink-600 hover:border-red-400 hover:text-red-600 gap-1.5"
          >
            <Trash2 className="w-3.5 h-3.5" /> Clear all
          </Button>
        </div>

        {/* Contact rows */}
        <div className="space-y-3">
          {rows.map((row) => (
            <div key={row.id} className="bg-white border border-ink-100 rounded-sm p-4 relative" data-testid={`row-${row.id}`}>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_1fr_1fr] gap-3 items-center">
                {/* LinkedIn URL */}
                <div>
                  <label className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 block mb-1">
                    LinkedIn URL
                  </label>
                  <div className="relative">
                    <Link2 className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-300" />
                    <input
                      type="text"
                      value={row.linkedin_url}
                      onChange={(e) => updateRow(row.id, { linkedin_url: e.target.value })}
                      placeholder="https://linkedin.com/in/jsnow"
                      data-testid={`linkedin-${row.id}`}
                      disabled={running || row.state === "pending"}
                      className="w-full h-9 pl-8 pr-2 rounded-sm border border-ink-200 text-sm focus:outline-none focus:border-ink-500 disabled:bg-ink-50"
                    />
                  </div>
                </div>

                <div className="hidden md:flex items-center justify-center text-[10px] font-mono uppercase tracking-widestx text-ink-400 px-1">
                  OR
                </div>

                {/* First name */}
                <div>
                  <label className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 block mb-1">
                    First name
                  </label>
                  <div className="relative">
                    <User className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-300" />
                    <input
                      type="text"
                      value={row.first_name}
                      onChange={(e) => updateRow(row.id, { first_name: e.target.value })}
                      placeholder="Jon"
                      data-testid={`first-${row.id}`}
                      disabled={running || row.state === "pending"}
                      className="w-full h-9 pl-8 pr-2 rounded-sm border border-ink-200 text-sm focus:outline-none focus:border-ink-500 disabled:bg-ink-50"
                    />
                  </div>
                </div>

                {/* Last name */}
                <div>
                  <label className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 block mb-1">
                    Last name
                  </label>
                  <div className="relative">
                    <User className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-300" />
                    <input
                      type="text"
                      value={row.last_name}
                      onChange={(e) => updateRow(row.id, { last_name: e.target.value })}
                      placeholder="Snow"
                      data-testid={`last-${row.id}`}
                      disabled={running || row.state === "pending"}
                      className="w-full h-9 pl-8 pr-2 rounded-sm border border-ink-200 text-sm focus:outline-none focus:border-ink-500 disabled:bg-ink-50"
                    />
                  </div>
                </div>

                {/* Domain */}
                <div>
                  <label className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 block mb-1">
                    Company domain
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-300" />
                    <input
                      type="text"
                      value={row.domain}
                      onChange={(e) => updateRow(row.id, { domain: e.target.value })}
                      placeholder="Stark.com"
                      data-testid={`domain-${row.id}`}
                      disabled={running || row.state === "pending"}
                      className="w-full h-9 pl-8 pr-2 rounded-sm border border-ink-200 text-sm focus:outline-none focus:border-ink-500 disabled:bg-ink-50"
                    />
                  </div>
                </div>
              </div>

              {/* Row result / actions */}
              <div className="mt-3 flex items-center justify-between gap-3">
                <div className="text-xs flex items-center gap-2 min-h-[20px]">
                  {row.state === "pending" && (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-ink-400" />
                      <span className="text-ink-500">Searching…</span>
                    </>
                  )}
                  {row.state === "settled" && (
                    <>
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      <span className="font-mono text-ink-900 font-semibold" data-testid={`email-${row.id}`}>
                        {row.email}
                      </span>
                      {row.email_status && (
                        <span className="text-[10px] uppercase tracking-widestx font-mono px-1.5 py-0.5 rounded-sm bg-ink-100 text-ink-600">
                          {row.email_status}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => copyEmail(row.email)}
                        className="text-ink-400 hover:text-ink-900"
                        data-testid={`copy-${row.id}`}
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                  {row.state === "refunded" && (
                    <>
                      <XCircle className="w-3.5 h-3.5 text-ink-400" />
                      <span className="text-ink-500">No email found · credit refunded</span>
                    </>
                  )}
                  {row.state === "error" && (
                    <>
                      <XCircle className="w-3.5 h-3.5 text-red-500" />
                      <span className="text-red-600">{row.reason || "Error"}</span>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeRow(row.id)}
                  data-testid={`remove-${row.id}`}
                  className="text-ink-300 hover:text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Add contact + LinkedIn callout */}
        <div className="mt-4 flex items-center justify-between gap-4">
          <Button
            variant="outline"
            onClick={addRow}
            disabled={running}
            data-testid="add-contact"
            className="h-9 rounded-sm border-ink-200 text-ink-700 hover:border-ink-400 gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Add Contact
          </Button>
          <div className="text-[11px] text-ink-500 bg-purple-50/50 border border-purple-100 rounded-sm px-3 py-2 max-w-md">
            <span className="font-semibold text-purple-800">Providing the LinkedIn URL gets you:</span>
            <span className="ml-1 text-ink-600">+15% more results · company and profile details</span>
          </div>
        </div>

        {/* Run button */}
        <div className="mt-8 flex items-center justify-between bg-white border border-ink-100 rounded-sm p-4 flex-wrap gap-3">
          <div className="text-sm text-ink-600">
            <span className="font-semibold text-ink-900">{validRowCount}</span> valid contact{validRowCount === 1 ? "" : "s"} ready ·
            costs <span className="font-mono text-ink-900">{validRowCount}</span> credit{validRowCount === 1 ? "" : "s"} ·
            you have <span className="font-mono text-ink-900">{isUnlimited ? "∞" : creditsRemaining}</span>
            {completedRows.length > 0 && (
              <span className="text-emerald-700 ml-2">
                · {foundCount}/{completedRows.length} found
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {completedRows.length > 0 && (
              <Button
                onClick={downloadCsv}
                disabled={running}
                variant="outline"
                data-testid="find-email-download-csv"
                className="h-10 rounded-sm border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-2"
                title="Download results as CSV"
              >
                <Download className="w-4 h-4" /> Download CSV
              </Button>
            )}
            <Button
              onClick={runAll}
              disabled={running || validRowCount === 0}
              data-testid="run-all"
              className="h-10 rounded-sm bg-ink-900 hover:bg-ink-800 text-white font-medium gap-2"
            >
              {running ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Searching…
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" /> Find emails
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
